/**
 * What the report may say about how it was made, read from what actually happened
 * (provider health, section statuses) rather than from a fixed list (NS-42).
 *
 * The evidence certificate and the Methodology used to name every provider the platform can
 * consult and an "8-step" pipeline whatever ran. A certificate that names a source that never
 * answered is wrong on its face, so these helpers return only the sources that answered, the
 * sources that were called and failed, and the sections that completed.
 */

/** Provider health as recorded by the investigation: `{ provider: { ok, failed } }`. */
export type ProviderHealthMap = Record<string, { ok?: number; failed?: number } | undefined> | null | undefined;

export interface SourcesRan {
  /** False when the run recorded no provider status at all. */
  recorded: boolean;
  answered: Array<{ provider: string; ok: number; failed: number }>;
  failedOnly: Array<{ provider: string; failed: number }>;
}

export function describeSourcesRan(health: ProviderHealthMap): SourcesRan {
  if (!health || typeof health !== 'object' || Object.keys(health).length === 0) {
    return { recorded: false, answered: [], failedOnly: [] };
  }
  const answered: SourcesRan['answered'] = [];
  const failedOnly: SourcesRan['failedOnly'] = [];
  for (const [provider, st] of Object.entries(health)) {
    const ok = Number(st?.ok ?? 0) || 0;
    const failed = Number(st?.failed ?? 0) || 0;
    if (ok > 0) answered.push({ provider, ok, failed });
    else if (failed > 0) failedOnly.push({ provider, failed });
  }
  answered.sort((a, b) => a.provider.localeCompare(b.provider));
  failedOnly.sort((a, b) => a.provider.localeCompare(b.provider));
  return { recorded: true, answered, failedOnly };
}

export interface StepsRun {
  total: number;
  /** Steps that produced a result. */
  completedCount: number;
  completed: string[];
  degraded: string[];
  notCompleted: string[];
  /** Where the figures come from: the backend coverage record, or the report's own section statuses. */
  basis: 'backend_coverage' | 'sections';
}

type SectionLike = { status?: string; health?: string } | undefined;
type CoverageLike = { steps_completed?: unknown; missing_steps?: unknown; degraded_steps?: unknown } | null | undefined;

/**
 * Steps that ran. The backend coverage record (`steps_completed`, `missing_steps`, `degraded_steps`)
 * is the authority when the run carries one; otherwise the report's section statuses are counted.
 */
export function describeStepsRun(sections: Record<string, SectionLike> | null | undefined, coverage?: CoverageLike): StepsRun {
  if (coverage && typeof coverage.steps_completed === 'number' && Array.isArray(coverage.missing_steps)) {
    const name = (x: unknown) => (x && typeof x === 'object' && 'step' in (x as object) ? String((x as { step: unknown }).step) : String(x));
    const missing = (coverage.missing_steps as unknown[]).map(name);
    const degraded = Array.isArray(coverage.degraded_steps) ? (coverage.degraded_steps as unknown[]).map(name) : [];
    return {
      total: coverage.steps_completed + missing.length,
      completedCount: coverage.steps_completed,
      completed: [],
      degraded,
      notCompleted: missing,
      basis: 'backend_coverage',
    };
  }
  const keys = Object.keys(sections || {});
  const completed: string[] = [];
  const degraded: string[] = [];
  const notCompleted: string[] = [];
  for (const k of keys) {
    const sec = (sections || {})[k];
    if (sec?.status === 'loaded') {
      completed.push(k);
      if (sec.health === 'degraded') degraded.push(k);
    } else notCompleted.push(k);
  }
  return { total: keys.length, completedCount: completed.length, completed, degraded, notCompleted, basis: 'sections' };
}

const label = (k: string) => k.replace(/_/g, ' ');

/** One sentence on the steps that ran, for the certificate and Methodology (plain text). */
export function stepsRunSentence(steps: StepsRun): string {
  if (steps.total === 0) return 'The investigation steps that ran were not recorded.';
  let s = `${steps.completedCount} of ${steps.total} investigation steps produced a result`;
  s += steps.completed.length ? `: ${steps.completed.map(label).join(', ')}.` : '.';
  if (steps.degraded.length) s += ` Completed with degraded data: ${steps.degraded.map(label).join(', ')}.`;
  if (steps.notCompleted.length) s += ` Did not complete: ${steps.notCompleted.map(label).join(', ')}.`;
  return s;
}

/**
 * Statute citations, one place. Notices are issued under BNSS section 94 (the 2023 Sanhita that
 * replaced CrPC section 91 from 1 July 2024); the electronic-record certificate is under section 63
 * of the Bharatiya Sakshya Adhiniyam, 2023 (the replacement for Evidence Act section 65B).
 *
 * OWNER: legal confirmation needed before external use. This picks the current statutes and names
 * the repealed provision once in brackets; if counsel prefers to cite only the older Evidence Act /
 * CrPC text (for records created before 1 July 2024), change these two constants and nothing else.
 */
export const NOTICE_STATUTE = 'Section 94 BNSS, 2023 (formerly Section 91 CrPC)';
export const CERTIFICATE_STATUTE =
  'Section 63 of the Bharatiya Sakshya Adhiniyam, 2023 (formerly Section 65B of the Indian Evidence Act, 1872)';
